import { useState, useEffect } from "react";
import {
  Mic,
  Play,
  Save,
  FolderOpen,
  Share2,
  MessageSquare,
  Maximize2,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const SLIDES = [
  {
    icon: Sparkles,
    iconColor: "text-indigo-500",
    iconBg: "bg-indigo-50",
    title: "Bem-vindo ao Audio Ideias",
    description:
      "Sua plataforma para capturar, organizar e ouvir suas ideias. Escreva, grave por voz ou cole textos — tudo em um só lugar.",
    visual: "welcome",
  },
  {
    icon: Play,
    iconColor: "text-emerald-500",
    iconBg: "bg-emerald-50",
    title: "Escreva e Ouça",
    description:
      "Digite ou cole qualquer texto e ouça em voz alta. Escolha o idioma, a velocidade e o gênero da voz. Ideal para revisar conteúdo, estudar ou simplesmente ouvir suas ideias.",
    visual: "tts",
  },
  {
    icon: Mic,
    iconColor: "text-rose-500",
    iconBg: "bg-rose-50",
    title: "Grave por Voz",
    description:
      "Clique no microfone e fale. Sua voz será convertida em texto automaticamente. Perfeito para capturar ideias rápidas enquanto está em movimento.",
    visual: "stt",
  },
  {
    icon: Save,
    iconColor: "text-amber-500",
    iconBg: "bg-amber-50",
    title: "Salve e Organize",
    description:
      "Salve suas ideias com título e categoria. Encontre qualquer ideia rapidamente usando a busca. Suas ideias ficam sincronizadas na nuvem.",
    visual: "save",
  },
  {
    icon: Share2,
    iconColor: "text-cyan-500",
    iconBg: "bg-cyan-50",
    title: "Compartilhe e Colabore",
    description:
      "Compartilhe ideias via link com qualquer pessoa. Receba feedback através do sistema de mensagens integrado. Controle quem pode ver e salvar suas ideias.",
    visual: "share",
  },
  {
    icon: Maximize2,
    iconColor: "text-purple-500",
    iconBg: "bg-purple-50",
    title: "Pronto para começar!",
    description:
      "Use o Modo Foco para escrever sem distrações. Fixe o painel de ideias ao lado para consultar enquanto trabalha. Clique no botão Tutorial a qualquer momento para rever essas dicas.",
    visual: "ready",
  },
];

function SlideVisual({ type }) {
  const visuals = {
    welcome: (
      <div className="flex items-center justify-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Mic className="w-6 h-6 text-indigo-500" />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300" />
        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Play className="w-6 h-6 text-emerald-500" />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300" />
        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
          <Save className="w-6 h-6 text-amber-500" />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300" />
        <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
          <Share2 className="w-6 h-6 text-cyan-500" />
        </div>
      </div>
    ),
    tts: (
      <div className="flex flex-col items-center gap-2">
        <div className="w-full max-w-xs h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center px-3">
          <span className="text-sm text-gray-400">Digite seu texto aqui...</span>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 rotate-90" />
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <Play className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="flex gap-0.5">
            {[3, 5, 7, 4, 6, 8, 5, 3, 6, 4].map((h, i) => (
              <div key={i} className="w-1 rounded-full bg-emerald-400" style={{ height: `${h * 3}px` }} />
            ))}
          </div>
        </div>
      </div>
    ),
    stt: (
      <div className="flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-full bg-rose-100 border-2 border-rose-300 flex items-center justify-center animate-pulse">
          <Mic className="w-7 h-7 text-rose-500" />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 rotate-90" />
        <div className="w-full max-w-xs h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center px-3">
          <span className="text-sm text-gray-500">Sua fala aparece como texto...</span>
        </div>
      </div>
    ),
    save: (
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-1.5">
          {["Projeto App", "Reunião", "Ideias livro"].map((t) => (
            <div key={t} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200">
              <FolderOpen className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs text-gray-600">{t}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    share: (
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
          <Share2 className="w-5 h-5 text-cyan-500" />
        </div>
        <div className="w-px h-8 bg-gray-200" />
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-indigo-500" />
        </div>
      </div>
    ),
    ready: (
      <div className="flex items-center gap-2">
        <div className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-600 text-xs font-medium flex items-center gap-1.5">
          <Maximize2 className="w-3.5 h-3.5" /> Modo Foco
        </div>
        <div className="px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-medium">
          Painel Fixo
        </div>
        <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-600 text-xs font-medium">
          Tutorial
        </div>
      </div>
    ),
  };
  return visuals[type] || null;
}

export function WelcomeTour({ onClose, onStartGuide }) {
  const [current, setCurrent] = useState(0);
  const slide = SLIDES[current];
  const isLast = current === SLIDES.length - 1;
  const Icon = slide.icon;

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && !isLast) setCurrent((c) => c + 1);
      if (e.key === "ArrowLeft" && current > 0) setCurrent((c) => c - 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [current, isLast, onClose]);

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden animate-scale-in"
        data-testid="welcome-tour-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors z-10"
          data-testid="welcome-tour-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="px-8 pt-10 pb-6">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl ${slide.iconBg} flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`w-8 h-8 ${slide.iconColor}`} />
          </div>

          {/* Visual */}
          <div className="flex justify-center mb-5 min-h-[60px]">
            <SlideVisual type={slide.visual} />
          </div>

          {/* Text */}
          <h2 className="text-xl font-bold text-gray-900 text-center mb-3 font-heading">
            {slide.title}
          </h2>
          <p className="text-sm text-gray-500 text-center leading-relaxed max-w-sm mx-auto">
            {slide.description}
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 flex items-center justify-between">
          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === current ? "w-6 bg-indigo-500" : "bg-gray-200 hover:bg-gray-300"
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            {current > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrent((c) => c - 1)}
                className="text-gray-500"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
            {isLast ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => { onClose(); onStartGuide?.(); }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="start-interactive-guide"
                >
                  Iniciar Guia Interativo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onClose}
                  data-testid="welcome-tour-done"
                >
                  Entendi!
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => setCurrent((c) => c + 1)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="welcome-tour-next"
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
